import { ChangeEvent, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ExpressCreditBill, ExpressCreditBillParams } from "../../types/express-document.type";
import { formatNumber } from "../../utils";
import { formatBuddhistShortDate } from "../../utils/date";
import CellTextField from "./CellTextField";
import CreditBillDialog from "./CreditBillDialog";
import { MONEY_DECIMALS } from "./constants";
import { BillLineDraft, sanitizeDecimal } from "./documentDraft";
import { gridHeadCellSx, gridHeadSx } from "./gridStyles";

interface BillLinesGridProps {
  billLines: BillLineDraft[];
  readOnly: boolean;
  /** Where the credit bills are read from; null until the customer is picked. */
  creditBillParams: ExpressCreditBillParams | null;
  error?: string;
  onChange: (billLines: BillLineDraft[]) => void;
  onPick: (bills: ExpressCreditBill[]) => void;
}

const PAY_AMOUNT_WIDTH = 130;

interface BillLineRowProps {
  line: BillLineDraft;
  readOnly: boolean;
  onUpdate: (line: BillLineDraft) => void;
  onRemove: (billNo: string) => void;
}

function BillLineRow({ line, readOnly, onUpdate, onRemove }: Readonly<BillLineRowProps>) {
  const handlePayAmountChange = (event: ChangeEvent<HTMLInputElement>) =>
    onUpdate({ ...line, payAmount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleRemove = () => onRemove(line.billNo);

  return (
    <TableRow>
      <TableCell>{line.billNo}</TableCell>
      <TableCell>{formatBuddhistShortDate(line.billDate)}</TableCell>
      <TableCell>{line.salespersonCode ?? "-"}</TableCell>
      <TableCell align="right">{formatNumber(line.billAmount)}</TableCell>
      <TableCell align="right">{formatNumber(line.outstandingAmount)}</TableCell>
      <TableCell align="right">
        <CellTextField
          value={line.payAmount}
          readOnly={readOnly}
          displayValue={formatNumber(line.payAmount)}
          ariaLabel={`ยอดชำระบิล ${line.billNo}`}
          isNumeric
          width={PAY_AMOUNT_WIDTH}
          onChange={handlePayAmountChange}
        />
      </TableCell>
      <TableCell>{formatBuddhistShortDate(line.dueDate) || "-"}</TableCell>
      <TableCell>{line.reference ?? "-"}</TableCell>
      {!readOnly && (
        <TableCell align="center">
          <Tooltip title="เอาบิลออก">
            <IconButton size="small" onClick={handleRemove} aria-label={`เอาบิล ${line.billNo} ออก`}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}

/** รายการบิลที่รับชำระ: the customer's credit bills an E1 receipt pays, and how much of each. */
export default function BillLinesGrid({
  billLines,
  readOnly,
  creditBillParams,
  error,
  onChange,
  onPick,
}: Readonly<BillLinesGridProps>) {
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const pickedBillNos = billLines.map((line) => line.billNo);

  const handleUpdate = (updated: BillLineDraft) =>
    onChange(billLines.map((line) => (line.billNo === updated.billNo ? updated : line)));
  const handleRemove = (billNo: string) => onChange(billLines.filter((line) => line.billNo !== billNo));
  const handleOpenPicker = () => setIsPickerOpen(true);
  const handleClosePicker = () => setIsPickerOpen(false);

  return (
    <Stack gap={1}>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead sx={gridHeadSx}>
            <TableRow>
              <TableCell sx={gridHeadCellSx}>ใบกำกับ#</TableCell>
              <TableCell sx={gridHeadCellSx}>วันที่</TableCell>
              <TableCell sx={gridHeadCellSx}>พนักงานขาย</TableCell>
              <TableCell sx={gridHeadCellSx} align="right">
                จำนวนเงิน
              </TableCell>
              <TableCell sx={gridHeadCellSx} align="right">
                ยอดค้างชำระ
              </TableCell>
              <TableCell sx={gridHeadCellSx} align="right">
                ยอดชำระ
              </TableCell>
              <TableCell sx={gridHeadCellSx}>ครบกำหนด</TableCell>
              <TableCell sx={gridHeadCellSx}>อ้างอิง</TableCell>
              {!readOnly && <TableCell width={56} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {billLines.map((line) => (
              <BillLineRow
                key={line.billNo}
                line={line}
                readOnly={readOnly}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {billLines.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          {creditBillParams ? "ยังไม่ได้เลือกบิล" : "เลือกลูกค้าก่อน แล้วเลือกบิลเครดิตที่รับชำระ"}
        </Typography>
      )}
      {!readOnly && (
        <Stack direction="row">
          <Button
            size="small"
            startIcon={<PlaylistAddIcon />}
            disabled={!creditBillParams}
            onClick={handleOpenPicker}
          >
            เลือกบิล
          </Button>
        </Stack>
      )}
      {creditBillParams && (
        <CreditBillDialog
          open={isPickerOpen}
          params={creditBillParams}
          pickedBillNos={pickedBillNos}
          onClose={handleClosePicker}
          onPick={onPick}
        />
      )}
    </Stack>
  );
}
